import { TCasesDocumentType } from "../../models/CaseDocumentType1";
import { TRoadblockCaseData, TRoadblockConfiguration } from "../roadblockTypesAndCodecs";
import { fact, formatEvidenceDate, neutralFact } from "../evidence/evidenceHelpers";
import { TRoadblockEvidenceLink } from "../../codecs/form/RoadblockForm";
import { formatCurrencyFromPence } from "../../../shared/src/util/numberFormatting";

const contractsLink: TRoadblockEvidenceLink = { block: "case_contracts", section: null };
const clientsLink: TRoadblockEvidenceLink = { block: "case_details", section: "clients" };

const auctionStatusDisplay = (status: "unknown" | "yes" | "no"): string =>
    status === "yes" ? "Auction" : status === "no" ? "Not an auction" : "Not set";

const anyReceivedDocumentExists = (c: TRoadblockCaseData, types: Array<TCasesDocumentType>) =>
    c.cases_documents.filter(
        ({type, received_date}) =>
            types.includes(type)
            && received_date !== null
    ).length > 0;

const osConveyancerDetailsAdded = (c: TRoadblockCaseData) => 
    c.case.other_side_conveyancer_organisation_name.trim() !== ""
    && c.case.other_side_conveyancer_name.trim() !== ""
    && c.case.other_side_conveyancer_phone_number !== null
    && c.case.other_side_conveyancer_phone_number.trim() !== ""
    && c.case.other_side_conveyancer_address.trim() !== ""
    && c.case.other_side_conveyancer_email_address !== null
    && c.case.other_side_conveyancer_email_address.trim() !== "";

export const propertyHasDetails = (p: TRoadblockCaseData["cases_properties"][number]) =>
    p.tenure !== "unknown"
    && p.postcode !== null
    && (p.building_name.trim() !== "" || p.building_number.trim() !== "")
    && p.street_name.trim() !== ""
    && p.is_registered !== null
    && p.transfer_of !== "unknown"
    && (!p.is_registered || (
        p.title_number.trim() !== ""
        && p.last_transfer_date !== null
    ))
    && p.title_guarantee !== "unknown"
    && p.title_class !== "unknown"
    && (p.tenure !== "leasehold" || p.affected_by_building_safety_act !== null);

export const propertyDetailsAdded = (c: TRoadblockCaseData) =>
    c.cases_properties.length > 0
    && c.cases_properties.every(propertyHasDetails)
    && c.case.has_management_company !== null;

export const Contracts: TRoadblockConfiguration = {
    type: "contracts",
    display_name: "Contracts",
    only_create_if_resolved: [],
    requires_authoriser_signoff: false,
    blocks_exchange: true,
    create: {
        create_condition_description: "Sale cases only",
        predicate: (c) =>
            c.case.transaction_type === "sale"
            && (
                c.cases_documents.filter(({ type, received_date }) => type === "mos" && received_date !== null).length > 0
                || c.case.no_estate_agent
            )
    },
    checklist: [
        {
            type: "offer_accepted",
            is_resolved_by_short_description: "Offer accepted",
            create: {
                create_condition_description: "All cases",
                predicate: () => true
            },
            resolve: {
                is_resolved_by_long_description: `Recording the date the offer was accepted on the case.`,
                predicate: (c) => c.case.is_offer_accepted !== null,
                evidence: (c) => [
                    fact(
                        "Offer accepted",
                        c.case.is_offer_accepted ? `Accepted ${formatEvidenceDate(c.case.is_offer_accepted)}` : "Not recorded",
                        c.case.is_offer_accepted !== null,
                    ),
                ],
            }
        },
        {
            type: "draft_contract_created",
            is_resolved_by_short_description: "Draft contract created",
            create: {
                create_condition_description: "All cases",
                predicate: () => true
            },
            resolve: {
                is_resolved_by_long_description: `A draft contract being created for the case.`,
                predicate: (c) => anyReceivedDocumentExists(c, ["draft_contract", "signed_contract"]),
                evidence: (c) => {
                    const present = anyReceivedDocumentExists(c, ["draft_contract", "signed_contract"]);
                    return [fact("Draft contract", present ? "Created" : "Not created", present, contractsLink)];
                }
            }
        },
        {
            type: "draft_tr1_created",
            is_resolved_by_short_description: "Draft Transfer created",
            create: {
                create_condition_description: "All cases",
                predicate: () => true
            },
            resolve: {
                is_resolved_by_long_description: `A draft or signed TR1/TP1/TP2/TR2 being created for the case.`,
                predicate: (c) => anyReceivedDocumentExists(c, ["draft_tr1", "signed_tr1", "draft_tp1", "signed_tp1", "draft_tp2", "signed_tp2", "draft_tr2", "signed_tr2"]),
                evidence: (c) => {
                    const present = anyReceivedDocumentExists(c, ["draft_tr1", "signed_tr1", "draft_tp1", "signed_tp1", "draft_tp2", "signed_tp2", "draft_tr2", "signed_tr2"]);
                    return [fact("Draft Transfer", present ? "Created" : "Not created", present, contractsLink)];
                }
            }
        },
        {
            type: "draft_contract_sent",
            is_resolved_by_short_description: "Draft contract sent",
            create: {
                create_condition_description: "Draft contract exists on the case",
                predicate: (c) => anyReceivedDocumentExists(c, ["draft_contract", "signed_contract"])
            },
            resolve: {
                is_resolved_by_long_description: `A draft contract being sent.`,
                predicate: (c) =>
                    c.case.sent_draft_contract !== null,
                evidence: (c) => [
                    fact(
                        "Draft contract sent",
                        c.case.sent_draft_contract !== null ? `Sent ${formatEvidenceDate(c.case.sent_draft_contract)}` : "Not sent",
                        c.case.sent_draft_contract !== null,
                        contractsLink,
                    ),
                ]
            }
        },
        {
            type: "os_conveyancer_details_added",
            is_resolved_by_short_description: "Other side conveyancer details added",
            create: {
                create_condition_description: "All cases",
                predicate: () => true
            },
            resolve: {
                is_resolved_by_long_description: `Setting all of the fields for other side conveyancer details.`,
                predicate: (c) => osConveyancerDetailsAdded(c),
                evidence: (c) => {
                    const org = c.case.other_side_conveyancer_organisation_name.trim();
                    const name = c.case.other_side_conveyancer_name.trim();
                    const phone = (c.case.other_side_conveyancer_phone_number || "").trim();
                    const email = (c.case.other_side_conveyancer_email_address || "").trim();
                    const address = c.case.other_side_conveyancer_address.trim();
                    return [
                        fact("Firm", org !== "" ? org : "Not set", org !== "", contractsLink),
                        fact("Contact", name !== "" ? name : "Not set", name !== "", contractsLink),
                        fact("Phone", phone !== "" ? phone : "Not set", phone !== "", contractsLink),
                        fact("Email", email !== "" ? email : "Not set", email !== "", contractsLink),
                        fact("Address", address !== "" ? address : "Not set", address !== "", contractsLink),
                    ];
                }
            }
        },
        {
            type: "verify_os_conveyancer",
            is_resolved_by_short_description: "Other side conveyancer verified",
            create: {
                create_condition_description: "All cases",
                predicate: (c) => osConveyancerDetailsAdded(c)
            },
            resolve: {
                is_resolved_by_long_description: `Setting the other side conveyancer's SRA/CLC numbers.`,
                predicate: (c) =>
                    c.case.other_side_conveyancer_sra_or_clc_number.trim() !== "",
                evidence: (c) => {
                    const sra = c.case.other_side_conveyancer_sra_or_clc_number.trim();
                    return [fact("SRA/CLC number", sra !== "" ? sra : "Not set", sra !== "", contractsLink)];
                }
            }
        },
        {
            type: "contract_details_added",
            is_resolved_by_short_description: "Contract details added",
            create: {
                create_condition_description: "All cases",
                predicate: () => true
            },
            resolve: {
                is_resolved_by_long_description: `Setting all of the contract details fields.`,
                predicate: (c) =>
                    c.case.price_pence !== null
                    && c.case.deposit_according_to_contract_pence !== null
                    && c.case.auction_status !== "unknown",
                evidence: (c) => [
                    fact(
                        "Price",
                        c.case.price_pence !== null ? formatCurrencyFromPence(c.case.price_pence) : "Not set",
                        c.case.price_pence !== null,
                        contractsLink,
                    ),
                    fact(
                        "Deposit",
                        c.case.deposit_according_to_contract_pence !== null ? formatCurrencyFromPence(c.case.deposit_according_to_contract_pence) : "Not set",
                        c.case.deposit_according_to_contract_pence !== null,
                        contractsLink,
                    ),
                    fact(
                        "Auction status",
                        auctionStatusDisplay(c.case.auction_status),
                        c.case.auction_status !== "unknown",
                        contractsLink,
                    ),
                ]
            }
        },
        {
            type: "estate_agent_details_added",
            is_resolved_by_short_description: "Estate agent details added",
            create: {
                create_condition_description: "All cases",
                predicate: () => true
            },
            resolve: {
                is_resolved_by_long_description: `Setting all of the estate agent fields`,
                predicate: (c) =>
                    (
                        c.case.estate_agent_organisation_name.trim() !== ""
                        && c.case.estate_agent_contact_name.trim() !== ""
                        && (c.case.estate_agent_phone_number || "").trim() !== ""
                        && (c.case.estate_agent_email_address || "").trim() !== ""
                    ) || c.case.no_estate_agent === true,
                evidence: (c) => {
                    if (c.case.no_estate_agent === true) {
                        return [neutralFact("Estate agent", "No estate agent on this case", clientsLink)];
                    }
                    const org = c.case.estate_agent_organisation_name.trim();
                    const name = c.case.estate_agent_contact_name.trim();
                    const phone = (c.case.estate_agent_phone_number || "").trim();
                    const email = (c.case.estate_agent_email_address || "").trim();
                    return [
                        fact("Agency", org !== "" ? org : "Not set", org !== "", clientsLink),
                        fact("Contact", name !== "" ? name : "Not set", name !== "", clientsLink),
                        fact("Phone", phone !== "" ? phone : "Not set", phone !== "", clientsLink),
                        fact("Email", email !== "" ? email : "Not set", email !== "", clientsLink),
                    ];
                }
            }
        },
        {
            type: "add_completion_deadline_for_auction_case",
            is_resolved_by_short_description: "Add completion deadline",
            create: {
                create_condition_description: "Auctions only",
                predicate: (c) => c.case.auction_status === "yes"
            },
            resolve: {
                is_resolved_by_long_description: `Setting a completion deadline for the case.`,
                predicate: (c) =>
                    c.case.completion_deadline !== null,
                evidence: (c) => [
                    fact(
                        "Completion deadline",
                        c.case.completion_deadline !== null ? formatEvidenceDate(c.case.completion_deadline) : "Not set",
                        c.case.completion_deadline !== null,
                        contractsLink,
                    ),
                ]
            }
        },
        {
            type: "add_os_client_details",
            is_resolved_by_short_description: "Add other side client details",
            create: {
                create_condition_description: "All cases",
                predicate: () => true
            },
            resolve: {
                is_resolved_by_long_description: `Checking the "all other side clients added" field, as well as either:
                    1. Setting all other side company details, or
                    2. Having other side clients exist with both a legal first and last name`,
                predicate: (c) => 
                    (
                        (
                            c.case.other_side_company_name !== ""
                            && c.case.other_side_company_number !== ""
                            && (
                                c.case.other_side_company_building_name !== ""
                                || c.case.other_side_company_building_number !== ""
                            )
                            && c.case.other_side_company_city_town !== ""
                            && c.case.other_side_company_country !== "unknown"
                            && c.case.other_side_company_postcode !== null
                            && c.case.other_side_company_postcode !== ""
                            && c.case.other_side_company_street_name !== ""
                            && c.users_other_side.length === 0
                        )
                        || c.users_other_side.filter(({legal_first_legal_name, legal_last_legal_name}) =>
                            legal_first_legal_name.trim() !== ""
                            && legal_last_legal_name.trim() !== ""
                        ).length > 0
                    )
                    && c.case.all_other_side_clients_added_and_right_to_buy_or_sell_checked,
                evidence: (c) => {
                    const namedOsClients = c.users_other_side.filter(
                        ({ legal_first_legal_name, legal_last_legal_name }) =>
                            legal_first_legal_name.trim() !== "" && legal_last_legal_name.trim() !== "",
                    );
                    const partyFacts = namedOsClients.length > 0
                        ? namedOsClients.map((u) => neutralFact("Other side client", `${u.legal_first_legal_name} ${u.legal_last_legal_name}`.trim(), contractsLink))
                        : c.case.other_side_company_name.trim() !== ""
                        ? [neutralFact("Other side company", c.case.other_side_company_name.trim(), contractsLink)]
                        : [fact("Other side party", "Not set", false, contractsLink)];
                    return [
                        ...partyFacts,
                        fact(
                            "All other side clients added",
                            c.case.all_other_side_clients_added_and_right_to_buy_or_sell_checked ? "Confirmed" : "Not confirmed",
                            c.case.all_other_side_clients_added_and_right_to_buy_or_sell_checked,
                            contractsLink,
                        ),
                    ];
                }
            }
        },
        {
            type: "auction_reservation_fee_answered",
            is_resolved_by_short_description: "Auction reservation fee answered",
            create: {
                create_condition_description: "Auctions only",
                predicate: (c) => c.case.auction_status === "yes",
            },
            resolve: {
                is_resolved_by_long_description: `Answering whether a reservation fee has already been paid on this auction lot. If the answer is yes, an uploaded "Reservation payment evidence" document is also required.`,
                predicate: (c) =>
                    c.case.reservation_fee_already_paid === false
                    || (
                        c.case.reservation_fee_already_paid === true
                        && anyReceivedDocumentExists(c, ["reservation_payment_evidence"])
                    ),
                evidence: (c) => {
                    const answered = c.case.reservation_fee_already_paid !== null;
                    const facts = [
                        fact(
                            "Reservation fee already paid",
                            c.case.reservation_fee_already_paid === null ? "Not answered" : c.case.reservation_fee_already_paid ? "Yes" : "No",
                            answered,
                            contractsLink,
                        ),
                    ];
                    if (c.case.reservation_fee_already_paid === true) {
                        const evidenceUploaded = anyReceivedDocumentExists(c, ["reservation_payment_evidence"]);
                        facts.push(fact("Payment evidence", evidenceUploaded ? "Uploaded" : "Not uploaded", evidenceUploaded, contractsLink));
                    }
                    return facts;
                },
            },
        },
    ]
};
